import BackendAPI from '~/api/BackendClient/BackendAPI'
import type { components } from '~/api/BackendClient/generated-schema'

export type CulturesPageResponse = components['schemas']['CulturesPageResponse']
export type CulturesPageRequest = components['schemas']['CulturesPageRequest']
export type CulturesPage_CulturePlate =
  components['schemas']['CulturesPage_CulturePlate']
export type PlateBarcodeFilter = components['schemas']['PlateBarcodeFilter']
export type FirstCheckedInAtFilter = components['schemas']['FirstCheckedInAtFilter']
export type LastCheckedOutAtFilter = components['schemas']['LastCheckedOutAtFilter']
export type PlateStatusFilter = components['schemas']['PlateStatusFilter']
export type LastGeneratedAtFilter = components['schemas']['LastGeneratedAtFilter']
export type CulturePageFilterDefinitionsResponse =
  components['schemas']['CulturePageFilterDefinitionsResponse']
export type CultureStatusFilter = components['schemas']['CultureStatusFilter']

export type PlateFilter =
  | PlateBarcodeFilter
  | FirstCheckedInAtFilter
  | LastCheckedOutAtFilter
  | PlateStatusFilter
  | CultureStatusFilter
  | LastGeneratedAtFilter

export type FilterDefinition =
  | components['schemas']['CultureStatusFilterDefinition']
  | components['schemas']['FirstCheckedInAtFilterDefinition']
  | components['schemas']['LastCheckedOutAtFilterDefinition']
  | components['schemas']['PlateStatusFilterDefinition']
  | components['schemas']['LastGeneratedAtFilterDefinition']

export type PlateStatusFilterDefinition =
  components['schemas']['PlateStatusFilterDefinition']

export type CultureStatusFilterDefinition =
  components['schemas']['CultureStatusFilterDefinition']

export type CulturePageCulturesFilterDefinitionResponse =
  components['schemas']['CulturePageBulkExportFilterDefinitionsResponse']

export type CultureFilter = CultureStatusFilter
export type CultureFilterDefinition = CultureStatusFilterDefinition
export type CulturesPageBulkExportRequest =
  components['schemas']['CulturesPageBulkExportRequest']
export type ObservationMetric = components['schemas']['ObservationMetric']

// Type-safe interfaces for unwrapped request bodies
// Django Ninja auto-wraps these based on parameter names in the schema
interface CulturesPageRequestBody {
  plate_filters: PlateFilter[]
  metrics?: ObservationMetric[]
}

interface CulturesPageBulkExportRequestBody {
  plate_filters: PlateFilter[]
  export_type: string
  include_all_fields: boolean
  metrics?: ObservationMetric[]
}

interface ValidatedBulkExportRequest {
  plate_filters: PlateFilter[]
  export_type: string
  include_all_fields: boolean
}

function validateBulkExportRequest(body: unknown): ValidatedBulkExportRequest {
  if (body === null || typeof body !== 'object') {
    throw new Error('Request body must be an object')
  }

  const { plate_filters, export_type, include_all_fields } = body as {
    plate_filters?: unknown
    export_type?: unknown
    include_all_fields?: unknown
  }

  if (!Array.isArray(plate_filters)) {
    throw new Error('plate_filters must be an array')
  }

  if (export_type !== undefined && typeof export_type !== 'string') {
    throw new Error('export_type must be a string')
  }

  if (include_all_fields !== undefined && typeof include_all_fields !== 'boolean') {
    throw new Error('include_all_fields must be a boolean')
  }

  return {
    plate_filters,
    export_type: export_type ?? 'csv',
    include_all_fields: include_all_fields ?? false,
  }
}

class CulturesPageAPI extends BackendAPI {
  getData = (
    request: CulturesPageRequest,
    pageSize: number = 50,
    pageToken?: string,
    metrics?: ObservationMetric[],
  ): Promise<CulturesPageResponse> => {
    // Django Ninja auto-wraps based on parameter name, so send unwrapped
    const requestBody: CulturesPageRequestBody = {
      plate_filters: request.plate_filters,
      metrics: metrics && metrics.length > 0 ? metrics : undefined,
    }
    return this.client.POST('/api/pages/cultures-page/', {
      body: requestBody,
      params: {
        query: {
          page_size: pageSize,
          page_token: pageToken,
        },
      },
    })
  }
  getFilterDefinitions = (): Promise<CulturePageFilterDefinitionsResponse> => {
    return this.client.GET('/api/filter-definitions/cultures-page/')
  }

  getCulturesFilterDefinitions =
    (): Promise<CulturePageCulturesFilterDefinitionResponse> => {
      return this.client.GET('/api/filter-definitions/cultures-page/bulk-export/')
    }

  getFilteredCultures = (
    body: unknown, // Accept unknown to enable runtime validation
    metrics?: ObservationMetric[],
  ): Promise<CulturesPageResponse> => {
    // Django Ninja auto-wraps based on parameter name, so send unwrapped
    // Runtime validation ensures type safety without 'as any' casts
    const validatedBody = validateBulkExportRequest(body)

    const requestBody: CulturesPageBulkExportRequestBody = {
      plate_filters: validatedBody.plate_filters,
      export_type: validatedBody.export_type,
      include_all_fields: validatedBody.include_all_fields,
      metrics: metrics && metrics.length > 0 ? metrics : undefined,
    }
    return this.client.POST('/api/pages/cultures-page/cultures/', {
      body: requestBody,
    })
  }
}

export default CulturesPageAPI
